/**
 * MiniCRM Service for handling form submissions to MiniCRM API
 */

import { getClientId, getSessionIdFromCookie } from './trackingService';
import { getCookie } from '../utils/tracking';

// Map form IDs to registration types - 5 types from FORM.md
const FORM_REGISTRATION_TYPES = {
    'footer-contact': 'Ajánlatkérő form',        // Footer = Offer Request Form (3447)
    'sticky-button-offer': 'Oldalsáv',       // Sticky = Green side bar (3448)
    'custom-offer': 'Egyedi ajánlatkérés',       // Custom = Custom Offer Request (3448)
    'email-request': 'Ajánlat emailben',         // Email = Offer via Email (3449)
    'contact-form': 'contact-form',              // Contact Form = Contact Form (3465)
    'callback': 'Visszahívás',                   // Callback request
};

/**
 * Extract gclid from cookies
 * @returns {string|null} - gclid value or null if not found
 */
function getGclid() {
    // First check _gclid cookie (direct gclid storage)
    const gclid = getCookie('_gclid');
    if (gclid) {
        return gclid;
    }

    // Fallback to _gcl_aw cookie
    const gclAwCookie = getCookie('_gcl_aw');
    if (gclAwCookie) {
        // Format: GCL.timestamp.gclid_value
        const parts = gclAwCookie.split('.');
        if (parts.length >= 3) {
            return parts[parts.length - 1]; // Get the last part
        }
    }

    return null;
}

/**
 * Collect additional tracking data from cookies
 * @returns {Promise<Object>} - Object containing tracking data
 */
async function collectTrackingData() {
    const trackingData = {};
    
    // Rakuten cookies mapping to UTM parameters
    const rakutenCookies = {
        'rakuten_site_id': 'utm_source',
        'rakuten_time_entered': 'utm_medium', 
        'rakuten_ran_mid': 'utm_campaign',
        'rakuten_ran_eaid': 'utm_term',
        'rakuten_ran_site_id': 'utm_content'
    };
    
    // Extract Rakuten cookies
    for (const [cookieName, crmField] of Object.entries(rakutenCookies)) {
        const cookieValue = getCookie(cookieName);
        if (cookieValue) {
            trackingData[crmField] = cookieValue;
        }
    }
    
    // Extract GA Client ID using tracking service (hashed format)
    const gaClientId = getClientId();
    if (gaClientId) {
        trackingData.ga_client_id = gaClientId;
    }
    
    // Extract GA Session ID as integer timestamp using tracking service
    const gaSessionId = getSessionIdFromCookie();
    if (gaSessionId) {
        trackingData.ga_session_id = gaSessionId;
    }
    
    // Extract gclid from cookies (proper method)
    const gclid = getGclid();
    if (gclid) {
        trackingData.gclid = gclid;
    }
    
    // Extract Facebook Pixel data from cookies
    const fbp = getCookie('_fbp');
    if (fbp) {
        trackingData.fbp = fbp;
    }
    
    const fbc = getCookie('_fbc');
    if (fbc) {
        trackingData.fbc = fbc;
    }
    
    // Extract user agent from browser
    if (navigator.userAgent) {
        trackingData.user_agent = navigator.userAgent;
    }
    
    // IP address will be handled server-side, so we don't collect it here
    // The backend will extract it from the request
    
    return trackingData;
}

/**
 * Submit form data to MiniCRM
 * @param {string} formId - The form identifier
 * @param {Object} formData - The form data to submit
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Response from MiniCRM API
 */
export async function submitToMiniCrm(formId, formData, options = {}) {
    try {
        // Skip MiniCRM submission for @netlizing.hu emails
        const email = formData.email || '';
        if (email.toLowerCase().endsWith('@netlizing.hu')) {
            return {
                status: 'fulfilled',
                value: {
                    success: true,
                    message: 'Netlizing internal email - skipped MiniCRM submission'
                }
            };
        }

        // Get registration type based on form ID
        const registrationType = FORM_REGISTRATION_TYPES[formId] || 'Oldalsáv';
        
        // Collect additional tracking data from cookies
        const additionalTrackingData = await collectTrackingData();
        
        // Prepare payload for MiniCRM API
        const payload = {
            form_id: formId,  // Send the form ID that backend expects
            company_name: formData.company_name || formData.name || '',
            first_name: formData.first_name || formData.forename || '',
            last_name: formData.last_name || formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            message: formData.message || '',
            url: formData.url || window.location.href,
            gdpr: formData.gdpr || formData.terms || false,
            ...additionalTrackingData
        };

        // Submit to MiniCRM API using XMLHttpRequest wrapped in Promise
        const result = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            
            xhr.open('POST', '/api/minicrm-submit', true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('X-CSRF-TOKEN', document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '');
            xhr.setRequestHeader('Accept', 'application/json');
            
            xhr.onreadystatechange = function() {
                if (xhr.readyState === 4) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        
                        if (xhr.status >= 200 && xhr.status < 300 && response.status === 'success') {
                            resolve({
                                success: true,
                                message: response.message || 'Successfully submitted to MiniCRM'
                            });
                        } else {
                            resolve({
                                success: false,
                                message: response.message || 'Failed to submit to MiniCRM'
                            });
                        }
                    } catch (parseError) {
                        resolve({
                            success: false,
                            message: 'Invalid response from MiniCRM'
                        });
                    }
                }
            };
            
            xhr.onerror = function() {
                resolve({
                    success: false,
                    message: 'Network error while submitting to MiniCRM'
                });
            };
            
            xhr.send(JSON.stringify(payload));
        });

        return {
            status: 'fulfilled',
            value: result
        };

    } catch (error) {
        return {
            status: 'fulfilled',
            value: {
                success: false,
                message: 'Error preparing MiniCRM request'
            }
        };
    }
}

export default {
    submitToMiniCrm
};